/* eslint-disable indent */
import { Action, createAction, handleActions } from 'redux-actions';
import { Dispatch } from 'redux';

import { SelectValue } from 'types/SelectValue';
import { InputFiled } from 'types/InputFiled';
import { FormDataMapping } from 'types/FormDataMapping';
import { LocationData } from 'types/LocationData';
import { FetchedData } from 'types/FetchedData';

import { useRedux } from 'util/hook/redux';

import { DeliveryType } from 'api/ride';
import { fetchResourceDeliveryLocationsFunc } from 'api/location';

import { GetState, State as GlobalState } from './reducers';

interface DeliveryWayFormData {
	deliveryType: SelectValue<DeliveryType | null>;
	deliveryStore: SelectValue<number | null>;
	country: SelectValue<string | null>;
	county: string;
	district: string;
	zipCode: string;
	address: string;
	storeState: SelectValue<number | null>;
	storeCity: SelectValue<number | null>;
	storeDistrict: SelectValue<number | null>;
}

export type DeliveryWayField = keyof DeliveryWayFormData;

type DeliveryWayForm = FormDataMapping<DeliveryWayFormData>;

export type State = {
	form: DeliveryWayForm;
	mappingValue: {
		delivery_type: DeliveryType | null;
		delivery_address: string;
		delivery_store_id: number | null;
	};
	locationOptions: {
		states: FetchedData<LocationData[]>;
		cities: FetchedData<LocationData[]>;
		districts: FetchedData<LocationData[]>;
	};
};

const initialState: State = {
	form: {
		deliveryType: { value: { label: '', value: null }, valid: true, error: '', required: true },
		deliveryStore: {
			value: { label: '', value: null },
			valid: true,
			error: '',
			required: true,
		},
		country: { value: { label: '', value: null }, valid: true, error: '', required: true },
		county: { value: '', valid: true, error: '', required: true },
		district: { value: '', valid: true, error: '', required: true },
		zipCode: { value: '', valid: true, error: '', required: true },
		address: { value: '', valid: true, error: '', required: true },
		storeState: { value: { label: '', value: null }, valid: true, error: '', required: false },
		storeCity: { value: { label: '', value: null }, valid: true, error: '', required: false },
		storeDistrict: { value: { label: '', value: null }, valid: true, error: '', required: false },
	},
	mappingValue: {
		delivery_type: null,
		delivery_address: '',
		delivery_store_id: null,
	},
	locationOptions: {
		states: { loading: false, error: '', data: [] },
		cities: { loading: false, error: '', data: [] },
		districts: { loading: false, error: '', data: [] },
	},
};

interface ChangeFormPayload {
	key: DeliveryWayField;
	data: Partial<InputFiled>;
}

const changeForm = createAction<ChangeFormPayload, ChangeFormPayload>(
	'CHANGE_DELIVERY_WAY_FORM',
	data => data,
);

const clearForm = createAction('CLEAR_DELIVERY_WAY_FORM');

type LocationOptionsPayload = Partial<FetchedData<LocationData[]>> & {
	tier: 'states' | 'cities' | 'districts';
};

interface FetchDeliveryLocationsParams {
	resourceType: 'ride' | 'course';
	resourceId: number;
	countryId: number;
	ancestorId: number;
}

export const fetchDeliveryStates = createAction<
	Promise<LocationOptionsPayload>,
	Pick<FetchDeliveryLocationsParams, 'resourceType' | 'resourceId' | 'countryId'>
>('FETCH_DELIVERY_STATES', async ({ resourceType, resourceId, countryId }) => {
	try {
		const { status, message, data } = await fetchResourceDeliveryLocationsFunc({
			resourceType,
			resourceId,
			countryId,
			location_type: 'STATE',
			ancestor_id: countryId,
		});
		if (status !== 200 && status !== 201) {
			throw new Error(message);
		}
		return { tier: 'states' as const, data };
	} catch (error) {
		return { tier: 'states' as const, error: error instanceof Error ? error.message : '' };
	}
});

export const fetchDeliveryCities = createAction<
	Promise<LocationOptionsPayload>,
	FetchDeliveryLocationsParams
>('FETCH_DELIVERY_CITIES', async ({ resourceType, resourceId, countryId, ancestorId }) => {
	try {
		const { status, message, data } = await fetchResourceDeliveryLocationsFunc({
			resourceType,
			resourceId,
			countryId,
			location_type: 'CITY',
			ancestor_id: ancestorId,
		});
		if (status !== 200 && status !== 201) {
			throw new Error(message);
		}
		return { tier: 'cities' as const, data };
	} catch (error) {
		return { tier: 'cities' as const, error: error instanceof Error ? error.message : '' };
	}
});

export const fetchDeliveryDistricts = createAction<
	Promise<LocationOptionsPayload>,
	FetchDeliveryLocationsParams
>('FETCH_DELIVERY_DISTRICTS', async ({ resourceType, resourceId, countryId, ancestorId }) => {
	try {
		const { status, message, data } = await fetchResourceDeliveryLocationsFunc({
			resourceType,
			resourceId,
			countryId,
			location_type: 'DISTRICT',
			ancestor_id: ancestorId,
		});
		if (status !== 200 && status !== 201) {
			throw new Error(message);
		}
		return { tier: 'districts' as const, data };
	} catch (error) {
		return { tier: 'districts' as const, error: error instanceof Error ? error.message : '' };
	}
});

export const getDeliveryWayMappingValue = createAction<
	(_: Dispatch, getState: GetState) => State['mappingValue']
>('GET_DELIVERY_WAY_MAPPING_VALUE', () => (_: Dispatch, getState: GetState) => {
	const {
		deliveryWay: {
			form: { deliveryType, deliveryStore, country, county, address, district, zipCode },
		},
	} = getState();
	let type = null;
	let addr = '';
	let id = null;

	switch (true) {
		case deliveryType.value.value === 'HOME_DELIVERY':
			switch (country.value.value) {
				case 'TW':
					type = deliveryType.value.value;
					addr = `${country.value.label} ${county.value} ${district.value} ${address.value}`;
					break;

				default:
					type = deliveryType.value.value;
					addr = `${country.value.label} ${zipCode.value} ${address.value}`;
					break;
			}
			break;
		case deliveryType.value.value === 'STORE':
			type = deliveryType.value.value;
			id = typeof deliveryStore.value.value === 'number' ? deliveryStore.value.value : null;
			break;
		case deliveryType.value.value === 'ON_SITE':
			type = deliveryType.value.value;
			break;
		default:
			break;
	}

	return {
		delivery_type: type,
		delivery_address: addr,
		delivery_store_id: id,
	};
});

export const reducer = {
	deliveryWay: handleActions<State, any>( // eslint-disable-line @typescript-eslint/no-explicit-any
		{
			CLEAR_DELIVERY_WAY_FORM: state => ({
				...state,
				form: initialState.form,
				locationOptions: initialState.locationOptions,
			}),

			CHANGE_DELIVERY_WAY_FORM: (state, action: Action<ChangeFormPayload>) => ({
				...state,

				form: {
					...state.form,
					[action.payload.key]: {
						...state.form[action.payload.key],
						...action.payload.data,
					},
				},
			}),

			GET_DELIVERY_WAY_MAPPING_VALUE: (state, action: Action<State['mappingValue']>) => ({
				...state,

				mappingValue: {
					...state.mappingValue,
					...action.payload,
				},
			}),

			FETCH_DELIVERY_STATES_PENDING: state => ({
				...state,
				locationOptions: {
					...state.locationOptions,
					states: { loading: true, error: '', data: [] },
				},
			}),

			FETCH_DELIVERY_STATES_FULFILLED: (state, action: Action<LocationOptionsPayload>) => ({
				...state,
				locationOptions: {
					...state.locationOptions,
					states: {
						loading: false,
						error: action.payload.error || '',
						data: action.payload.data || [],
					},
				},
			}),

			FETCH_DELIVERY_CITIES_PENDING: state => ({
				...state,
				locationOptions: {
					...state.locationOptions,
					cities: { loading: true, error: '', data: [] },
				},
			}),

			FETCH_DELIVERY_CITIES_FULFILLED: (state, action: Action<LocationOptionsPayload>) => ({
				...state,
				locationOptions: {
					...state.locationOptions,
					cities: {
						loading: false,
						error: action.payload.error || '',
						data: action.payload.data || [],
					},
				},
			}),

			FETCH_DELIVERY_DISTRICTS_PENDING: state => ({
				...state,
				locationOptions: {
					...state.locationOptions,
					districts: { loading: true, error: '', data: [] },
				},
			}),

			FETCH_DELIVERY_DISTRICTS_FULFILLED: (state, action: Action<LocationOptionsPayload>) => ({
				...state,
				locationOptions: {
					...state.locationOptions,
					districts: {
						loading: false,
						error: action.payload.error || '',
						data: action.payload.data || [],
					},
				},
			}),
		},
		initialState,
	),
};

const selectForm = (state: GlobalState) => state.deliveryWay.form;

const formActionsMap = {
	changeForm,
	clearForm,
	getDeliveryWayMappingValue,
};

export const useDeliveryWay = () =>
	useRedux<ReturnType<typeof selectForm>, typeof formActionsMap>(selectForm, formActionsMap);

const selectLocationOptions = (state: GlobalState) => state.deliveryWay.locationOptions;

const locationActionsMap = {
	fetchDeliveryStates,
	fetchDeliveryCities,
	fetchDeliveryDistricts,
};

export const useDeliveryWayLocations = () =>
	useRedux<ReturnType<typeof selectLocationOptions>, typeof locationActionsMap>(
		selectLocationOptions,
		locationActionsMap,
	);
